class AccordionHoverVideo {
  constructor() {
    this.container = document.querySelector('[data-accordion-container]');
    this.videoContainer = document.querySelector('[data-video-container]');
    
    if (!this.container || !this.videoContainer) return;
    
    this.accordionItems = this.container.querySelectorAll('[data-accordion-item]');
    this.videos = this.videoContainer.querySelectorAll('[data-accordion-video]');
    this.currentActiveId = null;
    this.isTransitioning = false;
    this.hoverTimeout = null;
    this.isSectionVisible = false;
    this.pausedByUser = false;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.section = this.videoContainer.closest('.section') || this.videoContainer;

    this.init();
  }

  init() {
    this.setupVisibilityObserver();
    this.setupMotionPreference();

    // Set first item as active by default
    if (this.accordionItems.length > 0) {
      const firstItemId = this.accordionItems[0].dataset.accordionItem;
      this.setActiveVideo(firstItemId);
    }

    // Add hover and click event listeners
    this.accordionItems.forEach(item => {
      const itemId = item.dataset.accordionItem;
      const accordionElement = item.querySelector('accordion-custom');
      const detailsElement = item.querySelector('details');
      
      // Hover functionality for desktop with debouncing
      item.addEventListener('mouseenter', () => {
        if (this.isTransitioning) return;
        
        clearTimeout(this.hoverTimeout);
        this.hoverTimeout = setTimeout(() => {
          this.setActiveVideo(itemId);
          this.setActiveAccordion(itemId);
        }, 100);
      });
      
      item.addEventListener('mouseleave', () => {
        clearTimeout(this.hoverTimeout);
      });
      
      // Click functionality for mobile and desktop
      detailsElement.addEventListener('click', (e) => {
        // Allow default details behavior but update active state
        setTimeout(() => {
          this.setActiveVideo(itemId);
          this.setActiveAccordion(itemId);
        }, 10);
      });
      
      // Prevent video change when clicking on summary (let accordion handle it)
      const summaryElement = detailsElement.querySelector('summary');
      summaryElement.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    });
    
    // Handle hover state for accordion opening
    this.setupHoverAccordion();
  }
  
  setupHoverAccordion() {
    this.accordionItems.forEach(item => {
      const detailsElement = item.querySelector('details');
      const accordionCustom = item.querySelector('accordion-custom');
      let hoverTimeout;
      
      // Only apply hover behavior on desktop
      if (window.innerWidth >= 1025) {
        item.addEventListener('mouseenter', () => {
          // Delay opening to prevent accidental triggers
          hoverTimeout = setTimeout(() => {
            if (!detailsElement.open) {
              detailsElement.open = true;
            }
          }, 200);
        });
        
        item.addEventListener('mouseleave', () => {
          clearTimeout(hoverTimeout);
          // Don't close on mouse leave - let user manually close or switch to another
        });
      }
    });
  }
  
  setupVisibilityObserver() {
    if (!('IntersectionObserver' in window)) {
      this.isSectionVisible = true;
      return;
    }

    this.visibilityObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        this.isSectionVisible = entry.isIntersecting && entry.intersectionRatio >= 0.25;

        const activeVideo = this.currentActiveId
          ? this.videoContainer.querySelector(`[data-accordion-video="${this.currentActiveId}"]`)
          : null;
        if (!activeVideo || activeVideo.tagName !== 'VIDEO') return;

        if (this.isSectionVisible && this.canAutoplay()) {
          activeVideo.play().catch(() => {});
        } else if (!this.isSectionVisible) {
          activeVideo.pause();
        }
      });
    }, { threshold: 0.25 });

    this.visibilityObserver.observe(this.section);
  }

  setupMotionPreference() {
    this.videos.forEach((video) => {
      if (video.tagName === 'VIDEO') {
        video.addEventListener('play', () => {
          this.pausedByUser = false;
        });
        video.addEventListener('pause', () => {
          if (this.isSectionVisible && video.classList.contains('active')) this.pausedByUser = true;
        });
      } else if (video.tagName === 'IFRAME' && video.classList.contains('active') && !this.reducedMotion.matches) {
        video.src = video.src.replace('autoplay=0', 'autoplay=1');
      }
    });

    this.reducedMotion.addEventListener('change', () => {
      if (!this.reducedMotion.matches) return;
      const activeVideo = this.videoContainer.querySelector('[data-accordion-video].active');
      if (activeVideo?.tagName === 'VIDEO') activeVideo.pause();
      else if (activeVideo?.tagName === 'IFRAME') this.pauseIframe(activeVideo);
    });
  }

  canAutoplay() {
    return !this.reducedMotion.matches && !this.pausedByUser;
  }

  setActiveVideo(itemId) {
    if (this.currentActiveId === itemId || this.isTransitioning) return;
    
    this.isTransitioning = true;
    
    // Find the target video first
    const activeVideo = this.videoContainer.querySelector(`[data-accordion-video="${itemId}"]`);
    if (!activeVideo) {
      this.isTransitioning = false;
      return;
    }
    
    // Hide all videos with smooth transition
    this.videos.forEach(video => {
      if (video === activeVideo) return; // Skip the one we're about to show
      
      video.classList.remove('active');
      
      // Pause videos when hiding
      if (video.tagName === 'VIDEO') {
        video.pause();
        // Safely reset currentTime
        try {
          if (video.readyState >= 1) { // HAVE_METADATA or higher
            video.currentTime = 0; // Reset to beginning
          }
        } catch (error) {
          // Fail silently if metadata not loaded
          // console.warn('Cannot reset video time:', error);
        }
      } else if (video.tagName === 'IFRAME') {
        // For iframes, use postMessage API instead of src manipulation
        this.pauseIframe(video);
      }
    });
    
    // Small delay to prevent flickering
    requestAnimationFrame(() => {
      // Show and play the active video
      activeVideo.classList.add('active');
      
      // Play video when showing
      if (activeVideo.tagName === 'VIDEO') {
        // Safely reset currentTime before playing
        try {
          if (activeVideo.readyState >= 1) { // HAVE_METADATA or higher
            activeVideo.currentTime = 0; // Start from beginning
          }
        } catch (error) {
          // Fail silently if metadata not loaded
          // console.warn('Cannot reset video time:', error);
        }
        if (this.isSectionVisible && this.canAutoplay()) {
          activeVideo.play().catch(e => {
            // console.log('Video autoplay prevented:', e);
          });
        }
      } else if (activeVideo.tagName === 'IFRAME' && this.canAutoplay()) {
        // For iframes, use postMessage API
        this.playIframe(activeVideo);
      }
      
      this.currentActiveId = itemId;
      
      // Allow new transitions after a brief delay
      setTimeout(() => {
        this.isTransitioning = false;
      }, 150);
    });
  }
  
  playIframe(iframe) {
    try {
      const src = iframe.src;
      if (src.includes('youtube.com')) {
        iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
      } else if (src.includes('vimeo.com')) {
        iframe.contentWindow.postMessage('{"method":"play"}', '*');
      }
    } catch (e) {
      // Fallback to src manipulation if postMessage fails
      const src = iframe.src;
      if (!src.includes('autoplay=1')) {
        iframe.src = src.replace('autoplay=0', 'autoplay=1');
      }
    }
  }

  pauseIframe(iframe) {
    try {
      const src = iframe.src;
      if (src.includes('youtube.com')) {
        iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      } else if (src.includes('vimeo.com')) {
        iframe.contentWindow.postMessage('{"method":"pause"}', '*');
      }
    } catch (e) {
      // Fallback to src manipulation if postMessage fails
      const src = iframe.src;
      if (src.includes('autoplay=1')) {
        iframe.src = src.replace('autoplay=1', 'autoplay=0');
      }
    }
  }

  setActiveAccordion(itemId) {
    // Update active styling on accordion items
    this.accordionItems.forEach(item => {
      if (item.dataset.accordionItem === itemId) {
        item.classList.add('active');
      } else {
        item.classList.remove('active');
      }
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new AccordionHoverVideo();
  });
} else {
  new AccordionHoverVideo();
}

// Reinitialize on Shopify section reload (theme editor)
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('[data-accordion-container]')) {
    new AccordionHoverVideo();
  }
});